import React, { useCallback, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@lemonadetraining/web-ui";
import { IconSparkles, IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import {
  RADAR_AXIS_DISPLAY_NAMES,
  SUGGESTION_FEEDBACK,
  type AICoachSuggestion,
  type SuggestionFeedback,
} from "@lemonadetraining/contracts";

export interface AICoachCardProps {
  suggestion: AICoachSuggestion;
  onFeedback: (suggestion: AICoachSuggestion, feedback: SuggestionFeedback) => void;
  onSeePlan: (suggestion: AICoachSuggestion) => void;
  onDismiss: (suggestion: AICoachSuggestion) => void;
}

const AICoachCard: React.FC<AICoachCardProps> = ({
  suggestion,
  onFeedback,
  onSeePlan,
  onDismiss,
}) => {
  // Server-stored feedback (`suggestion.feedback`) is authoritative; the local
  // `pending` flag locks the thumbs immediately on click so the user can't
  // double-submit before the Firestore listener round-trip lands.
  const [pending, setPending] = useState<SuggestionFeedback | null>(null);
  const effectiveFeedback = suggestion.feedback ?? pending;
  const feedbackLocked = effectiveFeedback !== null;

  const handleThumb = useCallback(
    (feedback: SuggestionFeedback) => {
      if (feedbackLocked) return;
      setPending(feedback);
      onFeedback(suggestion, feedback);
    },
    [feedbackLocked, onFeedback, suggestion]
  );
  const handleThumbsUp = useCallback(
    () => handleThumb(SUGGESTION_FEEDBACK.THUMBS_UP),
    [handleThumb]
  );
  const handleThumbsDown = useCallback(
    () => handleThumb(SUGGESTION_FEEDBACK.THUMBS_DOWN),
    [handleThumb]
  );
  const handleDismiss = useCallback(() => onDismiss(suggestion), [onDismiss, suggestion]);
  const handleSeePlan = useCallback(() => onSeePlan(suggestion), [onSeePlan, suggestion]);

  // Issue #23: the visible CTA copy ("See plan") is not descriptive out of
  // context for screen-reader users. Anchor it to the axis the suggestion is
  // about. `relatedAxis` is nullable on the type — fall back to the visible
  // text so the label is never empty/misleading.
  const ctaAriaLabel = suggestion.relatedAxis
    ? `${suggestion.ctaText} for improving ${RADAR_AXIS_DISPLAY_NAMES[suggestion.relatedAxis]}`
    : suggestion.ctaText;

  return (
    <div data-testid="ai-coach-live-region" aria-live="polite" aria-atomic="true">
      <Card
        data-testid="ai-coach-card"
        role="region"
        aria-label="AI Coach suggestion"
        className="border-l-4 border-l-primary"
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-wide text-primary">
            <IconSparkles size={14} aria-hidden="true" />
            <span>AI Coach</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1.5">
          <p className="text-sm font-semibold" data-testid="ai-coach-insight">
            {suggestion.insight}
          </p>
          <p className="text-sm text-muted-foreground" data-testid="ai-coach-reason">
            {suggestion.reason}
          </p>
          <p className="text-sm" data-testid="ai-coach-action">
            {suggestion.action}
          </p>
        </CardContent>
        <CardFooter className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label="Helpful suggestion"
              aria-pressed={effectiveFeedback === SUGGESTION_FEEDBACK.THUMBS_UP}
              disabled={feedbackLocked}
              onClick={handleThumbsUp}
              data-testid="ai-coach-thumbs-up"
            >
              <IconThumbUp size={16} aria-hidden="true" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label="Not helpful"
              aria-pressed={effectiveFeedback === SUGGESTION_FEEDBACK.THUMBS_DOWN}
              disabled={feedbackLocked}
              onClick={handleThumbsDown}
              data-testid="ai-coach-thumbs-down"
            >
              <IconThumbDown size={16} aria-hidden="true" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label="Dismiss suggestion"
              onClick={handleDismiss}
              data-testid="ai-coach-dismiss"
            >
              Dismiss
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSeePlan}
              data-testid="ai-coach-cta"
              aria-label={ctaAriaLabel}
            >
              {suggestion.ctaText}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default React.memo(AICoachCard);
